import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { dispatchRocEvent } from 'shared/hooks/useRocEventHandler';
import { getProductsForTable } from '../data';
import { useProductDetailsContext } from '../ProductDetailsContext';
import { FormatShapeDto, ProductTableProductDetails, Summary } from '../types';
import { ProductType } from '../../../../../../Roc.SharedClientApp/types/commerce/product-details';
import Spinner from 'shared/components/Spinner';
import RocOptionsTable from 'shared/components/roc-options-table/RocOptionsTable';
import { Localizer } from 'services/Localizer';
import { AddToCartResponse } from 'services/UserData.Commerce';
import useShoppingCart, { UseShoppingCart } from '../../shopping-cart/useShoppingCart';
import { OrderMessageAction } from '../../shopping-cart/OrderMessages';
import Modal from 'shared/components/Modal';
import CheckCircleSVG from 'shared/components/svg/CheckCircleSVG';
import AddedToCartAlert from '../../store/AddedToCartAlert';
import ProductPriceList from '../ProductPriceList';
import { GetMinimumOrderQuantity, ParseQuantityRange } from '../../pricing/pricing-result';
import { DisplayPrice, VariantPrice } from '../../../../../../Roc.SharedClientApp/types/commerce/display-price';
import { Prices, PricingLine } from '../../../../../../Roc.SharedClientApp/types/commerce/pricing-result';
import ProductTableFormatShapeSelector from './ProductTableFormatShapeSelector';
import { useTapeCaseProductDetailsContext } from './TapeCaseProductDetailsContext';
import TableFilter from 'react-table-filter';
import { ProductTableProductDetailsRow } from './useTapeCaseProductDetails';
import { CIRCLE_SHAPE, CIRCLE_WITH_HOLE_SHAPE, mapProductDetails, resolveColumnNames } from './data';
import { ProductCalculatorSelector } from './ProductCalculatorSelector';

function ProductSpecComp({ children }: any) {
	const elem = document.getElementById('alternate-product-specs');

	if (elem) {
		return ReactDOM.createPortal(children, elem);
	}
	return null;
}

/**
 * The main component for rendering Product Table.
 */
export function ProductTable() {
	const {
		state: { productId, productDetails },
	} = useProductDetailsContext();

	const {
		state: { productSeriesTableData },
		actor: { setProductSeriesTableData },
	} = useTapeCaseProductDetailsContext();

	const [isLoading, setIsLoading] = useState(false);
	const [selectedFormatShape, setFormatShape] = useState<FormatShapeDto | null>(null);
	const [isFiltering, setIsFiltering] = useState(false);
	const dimensionColumnNames = resolveColumnNames(selectedFormatShape);
	const [hideDimension1, setHideDimension1] = useState(false);
	const [hideDimension2, setHideDimension2] = useState(false);
	const [hideColor, setHideColor] = useState(false);

	/**
	 * A callback used by react-table-filter to update list of products when searching/filtering/sorting
	 */
	const tableFilterUpdated = (newData: ProductTableProductDetailsRow[]) => {
		setIsFiltering(true);
		setProductSeriesTableData(newData);
	};

	useEffect(() => {
		if (
			productDetails.summary.type != ProductType.Group ||
			productDetails.availableFormatShape == null ||
			productDetails.availableFormatShape.length == 0
		) {
			setProductSeriesTableData(null);
			return;
		}

		if (selectedFormatShape == null && productDetails.availableFormatShape.length > 0) {
			setFormatShape(productDetails.availableFormatShape[0]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [productDetails.availableFormatShape]);

	useEffect(() => {
		if (selectedFormatShape == null) {
			return;
		}

		async function fetchTableData() {
			if (selectedFormatShape != null) {
				setIsLoading(true);
				setProductSeriesTableData(null);
				const results = await getProductsForTable(productId, selectedFormatShape.id);
				const tableData = results.map((details) => {
					return mapProductDetails(details);
				});

				setHideDimension1(tableData.every((a) => !a.dimension1FilterValue));
				setHideDimension2(tableData.every((a) => !a.dimension2FilterValue));
				setHideColor(tableData.every((a) => !a.colorFilterValue));
				setProductSeriesTableData(tableData);
				setIsLoading(false);
			}
		}

		fetchTableData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedFormatShape]);

	useEffect(() => {
        const additionalProductInfoLink = document.querySelector(
            `[data-testid="product-page-additional-product-info-link"]`,
        );
        if (additionalProductInfoLink == null || !additionalProductInfoLink.classList.contains('roc-hidden')) {
			// link element not found or it's already displayed - no need for additional manipulation on the client-side
			return;
		}

		// this is the case when Additional Product Info link is hidden
		// we need to check if there are any tabs loaded on the client side
		if (productDetails.tabs != null && productDetails.tabs.some((t) => t.model != null || t.rawHtml != null)) {
			additionalProductInfoLink.classList.remove('roc-hidden');
		}
	}, [productDetails.tabs]);

	const alternateProductDetailsDom = document.getElementById('alternate-product-details-simples');
	if (!alternateProductDetailsDom) {
		console.warn('alternate product details DOM element not found.');
		return null;
	}

	const hasProductsForTable = productSeriesTableData != null && Object.keys(productSeriesTableData).length > 0;

	const alternateProductSpecsDomElement = document.getElementById('alternate-product-specs');
	if (
		alternateProductSpecsDomElement != null &&
		productDetails.availableCalculators != undefined &&
		productDetails.availableCalculators.length > 0
	) {
		alternateProductSpecsDomElement.classList.remove('roc-hidden');
	}

	const productSpecs = (
		<>
			{(hasProductsForTable || isFiltering) &&
			productDetails.availableCalculators != undefined &&
			productDetails.availableCalculators != null &&
			productDetails.availableCalculators.length > 0 ? (
				<ProductCalculatorSelector availableCalculators={productDetails.availableCalculators} />
			) : null}
		</>
	);

	const simplesListing = (
		<>
			<ProductSpecComp>{productSpecs}</ProductSpecComp>
			{productDetails.availableFormatShape != undefined && productDetails.availableFormatShape != null ? (
				<ProductTableFormatShapeSelector
					availableFormatShape={productDetails.availableFormatShape}
					selectedFormatShapeId={selectedFormatShape?.id ?? null}
					changeSelectedFormatShape={setFormatShape}
				/>
			) : null}
			{hasProductsForTable || isFiltering ? (
				<RocOptionsTable>
					{
						<thead>
							<TableFilter rows={productSeriesTableData} onFilterUpdate={tableFilterUpdated}>
								<th rowSpan={2}>Size / Description</th>
								<th rowSpan={2}>Type</th>
								{!hideDimension1 ? (
									<th rowSpan={2} filterkey="dimension1FilterValue">
										{dimensionColumnNames[0]}
									</th>
								) : null}
								{dimensionColumnNames[1] != null && !hideDimension2 ? (
									<th rowSpan={2} filterkey="dimension2FilterValue">
										{dimensionColumnNames[1]}
									</th>
								) : null}
								{!hideColor ? (
									<th rowSpan={2} filterkey="colorFilterValue">
										Color
									</th>
								) : null}

								<th rowSpan={2}>Pricing</th>
								<th rowSpan={2}>Qty</th>
							</TableFilter>
						</thead>
					}
					<tbody>
						{productSeriesTableData != null && productSeriesTableData.length > 0 ? (
							productSeriesTableData.map((row) => {
								{
									return (
										<ProductTableRow
											key={row.details.id}
											item={row.details}
											dimensionColumnNames={dimensionColumnNames}
											hideDimension1={hideDimension1}
											hideDimension2={hideDimension2}
											hideColor={hideColor}
										/>
									);
								}
							})
						) : isFiltering ? (
							<tr>
								<td colSpan={9} className="no-results">
									No results
								</td>
							</tr>
						) : null}
					</tbody>
				</RocOptionsTable>
			) : isLoading ? (
				<Spinner />
			) : null}
		</>
	);

	return ReactDOM.createPortal(simplesListing, alternateProductDetailsDom);
}

interface ProductTableRowProps {
	item: ProductTableProductDetails;
	dimensionColumnNames: [string, string | null];
	hideDimension1: boolean;
	hideDimension2: boolean;
	hideColor: boolean;
}

/**
 * The interface that combines available prices for the given pricing line and information about number of all prices.
 */
interface PricingLineDetails {
	numberOfPrices: number;
	prices: Prices<DisplayPrice<VariantPrice, VariantPrice>>;
}

/**
 * The interface that represents simiplified information about product pricing lines.
 */
interface ProductPricingLines {
	[key: string]: PricingLineDetails;
}

export function formatPricingLine(
	quantityRange: string,
	pricingLine: PricingLine<DisplayPrice<VariantPrice, VariantPrice>>,
	hideRange: boolean,
) {
	const [min, max] = ParseQuantityRange(quantityRange);
	return (
		<>
			{hideRange ? null : max == Infinity ? (
				<>
					<span>
						{min}
						{'+'}
					</span>
				</>
			) : (
				<>
					<span>
						{min} - {max} -{' '}
					</span>
				</>
			)}
			<span>
				<ProductPriceList key={pricingLine.id + min} pricingLine={pricingLine} quantity={min} />
			</span>
		</>
	);
}

function ProductTableRow(props: ProductTableRowProps) {
	const { item, dimensionColumnNames, hideDimension1, hideDimension2, hideColor } = props;
	const minimumOrderQuantity = GetMinimumOrderQuantity(item.pricingLine[0]);
	const [quantity, setQuantity] = useState<number | null>(minimumOrderQuantity);

	const pricing = {} as ProductPricingLines;
	for (const index in item.pricingLine) {
		const pricingLine = item.pricingLine[index];
		const pricesPerPricingLine = pricingLine.prices;
		pricing[index] = {
			numberOfPrices: Object.keys(pricesPerPricingLine).length,
			prices: pricesPerPricingLine,
		};
	}

	if (!item) {
		return null;
	}

	return (
		<tr>
			<td data-head="Size / Description">
				<ItemDescription itemId={item.id} summary={item.summary} item={item} />
			</td>
			<td data-head="Type">{item.formatShape}</td>

			{/* Dimensions (Width/Length or Diameter or Diameter/Inner Hole Diameter) */}
			<DimensionsColumns
				item={item}
				dimensionColumnNames={dimensionColumnNames}
				hideDimension1={hideDimension1}
				hideDimension2={hideDimension2}
			/>

			{!hideColor ? <td data-head="Color">{item.color}</td> : null}

			{!item.summary.isDiscontinued && (
				<td data-head="Pricing">
					{item.pricingLine.map((pricingLine, index) => (
						<div key={pricingLine.id}>
							{Object.keys(pricing[index].prices).map((p) => (
								<>{formatPricingLine(p, pricingLine, pricing[index].numberOfPrices == 1)}</>
							))}
						</div>
					))}
				</td>
			)}

			{!item.summary.isDiscontinued && (
				<td className="roc-product-detail-table__col-quantity" data-head="Qty">
					<QuantityInput
						item={item}
						onQuantityUpdate={(quantity) => {
							setQuantity(quantity);
						}}
						minimumOrderQuantity={minimumOrderQuantity}
					/>

					<AddToCartButton
						itemId={item.id}
						pricingLineId={item.pricingLine[0].id}
						quantity={quantity}
						minimumOrderQuantity={minimumOrderQuantity}
						isDiscontinued={item.summary.isDiscontinued}
					/>
				</td>
			)}

			{item.summary.isDiscontinued && (
				<td colSpan={2}>
					<p>{Localizer('QuickOrderDiscontinuedMessage')}</p>
				</td>
			)}
		</tr>
	);
}

interface DimensionsColumnsProps {
	item: ProductTableProductDetails;
	dimensionColumnNames: [string, string | null];
	hideDimension1: boolean;
	hideDimension2: boolean;
}

function DimensionsColumns(props: DimensionsColumnsProps) {
	const { item, dimensionColumnNames, hideDimension1, hideDimension2 } = props;
	switch (item.formatShape) {
		case CIRCLE_SHAPE:
			return (
				<>
					{!hideDimension1 ? (
						<td data-head={dimensionColumnNames[0]}>
							<p>
								{item.diameter ? <span>{item.diameter}</span> : null}
								{item.diameterUom ? <span>{item.diameterUom}</span> : null}
							</p>
						</td>
					) : null}
				</>
			);
		case CIRCLE_WITH_HOLE_SHAPE:
			return (
				<>
					{!hideDimension1 ? (
						<td data-head={dimensionColumnNames[0]}>
							<p>
								{item.diameter ? <span>{item.diameter}</span> : null}
								{item.diameterUom ? <span> {item.diameterUom}</span> : null}
							</p>
						</td>
					) : null}

					{!hideDimension2 ? (
						<td data-head={dimensionColumnNames[1]}>
							<p>
								{item.innerHoleDiameter ? <span>{item.innerHoleDiameter}</span> : null}
								{item.innerHoleDiameterUom ? <span> {item.innerHoleDiameterUom}</span> : null}
							</p>
						</td>
					) : null}
				</>
			);
		default:
			return (
				<>
					{!hideDimension1 ? (
						<td data-head={dimensionColumnNames[0]}>
							<p>
								{item.width ? <span>{item.width}</span> : null}
								{item.widthUom ? <span> {item.widthUom}</span> : null}
							</p>
						</td>
					) : null}
					{!hideDimension2 ? (
						<td data-head={dimensionColumnNames[1]}>
							<p>
								{item.length ? <span>{item.length}</span> : null}
								{item.lengthUom ? <span> {item.lengthUom}</span> : null}
							</p>
						</td>
					) : null}
				</>
			);
	}
}

interface ItemDescriptionProps {
	itemId: string;
	summary: Summary;
	item: ProductTableProductDetails;
}

function ItemDescription(props: ItemDescriptionProps) {
	const { itemId, summary, item } = props;

	return (
		<div className="roc-options-table__image-title-wrapper">
			<div className="roc-options-table__image-title-wrapper-item">
				{item.sizeDescription ? (
					<div className="roc-options-table__text">
						<span>Size: </span>
						<span>{item.sizeDescription}</span>
					</div>
				) : null}

				{summary.sku ? (
					<div className="roc-options-table__text">
						<span>SKU: </span>
						{summary.productUrl ? (
							<a
								href={summary.productUrl}
								onClick={() => {
									if (itemId) {
										dispatchRocEvent('product-clicked', {
											productId: itemId,
											url: summary?.productUrl ?? '#',
											productName: summary?.name ?? '',
											productGroupId: summary.productGroupId,
										});
									}
								}}
							>
								<span>{summary.sku}</span>
							</a>
						) : (
							<span>{summary.sku}</span>
						)}
					</div>
				) : (
					<span>{summary.sku}</span>
				)}

				{item.upc ? (
					<div className="roc-options-table__text">
						<span>UPC: </span>
						<span>{item.upc}</span>
					</div>
				) : null}

				{item.additionalSkuDescription ? (
					<div className="roc-options-table__text">
						<span>Additional SKU Description: </span>
						<span>{item.additionalSkuDescription}</span>
					</div>
				) : null}

				{item.distributorPartNumber ? (
					<div className="roc-options-table__text">
						<span>Your Part#: </span>
						<span>{item.distributorPartNumber}</span>
					</div>
				) : null}
			</div>
		</div>
	);
}

interface AddToCartButtonProps {
	itemId: string;
	quantity: number | null;
	pricingLineId: string;
	minimumOrderQuantity: number | null;
	isDiscontinued: boolean;
}

function AddToCartButton(props: AddToCartButtonProps) {
	const { itemId, quantity, pricingLineId, minimumOrderQuantity, isDiscontinued } = props;
	const [modalOpen, setModalOpen] = useState(true);
	const [addingToCart, setAddingToCart] = useState(false);
	const [addToCartResponse, setAddToCartResponse] = useState<AddToCartResponse | null>(null);

	const UseShoppingCartResult: UseShoppingCart = useShoppingCart();
	const {
		actor: { addToCart },
		state: { errorMessage },
	} = UseShoppingCartResult;

	const handleAddToCart = async () => {
		setAddToCartResponse(null);
		try {
			setAddingToCart(true);

			if (quantity == null || quantity == undefined) {
				return;
			}
			const response = await addToCart({
				itemId: itemId,
				productPricingLineId: pricingLineId,
				quantity: quantity,
			});

			setAddToCartResponse(response);

			if (response != null) {
				const orderErrorMessages = response.orderMessages?.filter(
					(om) =>
						om.messageAction === OrderMessageAction.Error ||
						om.messageAction === OrderMessageAction.RedirectToCart,
				);

				if (orderErrorMessages && orderErrorMessages.length > 0) {
					setModalOpen(false);
				} else {
					setModalOpen(true);
				}

				dispatchRocEvent('roc-cart-summary-updated', { cartSummary: response.cartSummary });
			}
		} catch (error) {
			setAddToCartResponse(null);
		}

		setAddingToCart(false);
	};

	return (
		<>
			<button
				onClick={() => handleAddToCart()}
				type="button"
				className="roc-btn roc-btn--block roc-btn--big"
				disabled={
					addingToCart ||
					quantity == null ||
					(minimumOrderQuantity != null && quantity != null && quantity < minimumOrderQuantity) ||
					isDiscontinued
				}
				data-testid="add-to-cart-button"
			>
				{Localizer('AddToCart')}
				{addingToCart && <Spinner />}
			</button>

			{addToCartResponse !== null ? (
				<Modal
					isOpen={modalOpen}
					renderTitle={() => Localizer('AddedToCart')}
					closeModal={() => setModalOpen(false)}
					titleIcon={<CheckCircleSVG />}
				>
					<AddedToCartAlert response={addToCartResponse} />
				</Modal>
			) : null}

			{errorMessage != null ? <p>{errorMessage}</p> : null}
		</>
	);
}

interface QuantityInputProps {
	item: ProductTableProductDetails;
	onQuantityUpdate?: (newQuantity: number | null) => void;
	minimumOrderQuantity: number | null;
}

function QuantityInput(props: QuantityInputProps) {
	const [quantity, setQuantity] = useState<number | null>(null);
	const { onQuantityUpdate, minimumOrderQuantity, item } = props;

	const updateQuantity = (e: React.ChangeEvent<HTMLInputElement>): void => {
		const newQuantity = parseInt(e.currentTarget.value);

		if (isNaN(newQuantity)) {
			setQuantity(null);
			if (onQuantityUpdate) {
				onQuantityUpdate(null);
			}
			return;
		}

		if (newQuantity > 0) {
			setQuantity(newQuantity);
			if (onQuantityUpdate) {
				onQuantityUpdate(newQuantity);
			}
		}
	};

	return (
		<div className="roc-options-table__form-group">
			<>
				<label htmlFor="quantity" className="roc-options-table__form-label">
					<input
						name="quantity"
						type="number"
						autoComplete="off"
						id="quantity"
						tabIndex={-1}
						min={minimumOrderQuantity ?? 1}
						className="roc-form-input__input roc-options-table__form-input roc-options-table__form-input--small"
						defaultValue={
							quantity === null ? (minimumOrderQuantity != null ? minimumOrderQuantity : '') : quantity
						}
						onChange={(e) => updateQuantity(e)}
						disabled={item.summary.isDiscontinued}
					/>
				</label>
				{minimumOrderQuantity != null ? <div>MOQ: {minimumOrderQuantity}</div> : null}
			</>
		</div>
	);
}
